import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Card, CardContent, CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import VideocamIcon from "@mui/icons-material/Videocam";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../api";

/**
 * Start class page when tutor clicks "Start class" from a course.
 * Creates a classroom for that course and loads Jitsi Meet in this page.
 */
export default function TutorStartClass() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subjects, setSubjects] = useState([]);
  const [subjectId, setSubjectId] = useState("");
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [creating, setCreating] = useState(false);
  const [opened, setOpened] = useState(false);
  const [error, setError] = useState(null);

  const tutorName = localStorage.getItem("tutor_name") || "Tutor";

  const getJitsiMeetingUrl = (code, name) => {
    const base = `https://meet.jit.si/${code}`;
    if (!name || !String(name).trim()) return base;
    return `${base}#userInfo.displayName=${encodeURIComponent(JSON.stringify(String(name).trim()))}`;
  };

  useEffect(() => {
    if (!courseId) {
      setLoading(false);
      setError("Missing course in URL.");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(`tutor/courses/${encodeURIComponent(courseId)}`)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.success && res.data.data) {
          setCourse(res.data.data);
        } else {
          setCourse(null);
          setError(res.data?.message || "Could not load course details.");
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setCourse(null);
        setError(e.response?.data?.message || "Could not load course details.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // Load subjects this tutor can conduct for the course.
    setLoadingSubjects(true);
    api
      .get(`tutor/courses/${encodeURIComponent(courseId)}/subjects`)
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.success && Array.isArray(res.data.data) ? res.data.data : [];
        setSubjects(list);
        if (list.length) setSubjectId(String(list[0].id));
      })
      .catch(() => {
        if (cancelled) return;
        setSubjects([]);
        setSubjectId("");
      })
      .finally(() => {
        if (!cancelled) setLoadingSubjects(false);
      });

    return () => {
      cancelled = true;
    };
  }, [courseId]);

  const handleStartMeeting = async () => {
    if (!courseId) return;
    if (!subjectId) {
      setError("Please select a subject.");
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const res = await api.post("tutor/classrooms", {
        course_id: Number(courseId),
        subject_id: Number(subjectId),
        name: course ? `${course.name} - Live` : "Live class",
      });
      const code = res.data?.classroom?.room_code;
      if (code) {
        window.open(getJitsiMeetingUrl(code, tutorName), "_blank", "noopener,noreferrer");
        setOpened(true);
      }
    } catch (e) {
      setError(e.response?.data?.message || "Failed to create meeting");
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
        Start class
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {course?.name ? <>Course: {course.name}. </> : null}
        Create a live meeting and it will open in a new tab. Students can join from their Classes section.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 260 }} required>
              <InputLabel>Subject</InputLabel>
              <Select
                value={subjectId}
                label="Subject"
                onChange={(e) => setSubjectId(e.target.value)}
                disabled={loadingSubjects || subjects.length === 0}
              >
                {subjects.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={<VideocamIcon />}
            onClick={handleStartMeeting}
            disabled={creating || !course || loadingSubjects || !subjectId}
            sx={{
              background: "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)",
              "&:hover": { opacity: 0.9 },
            }}
          >
            {creating ? "Creating meeting…" : "Start meeting"}
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Click to create a Jitsi Meet room; it will open in a new tab.
          </Typography>
          {opened && (
            <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
              Meeting opened in a new tab.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Button
        variant="outlined"
        onClick={() => navigate("/tutor/courses")}
        sx={{ mt: 2 }}
      >
        Back to My Courses
      </Button>
    </Box>
  );
}
